import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { session, isSuperuser, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const expired = searchParams.get("expired") === "true";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // After login, when the auth context resolves, surface the non-superuser case inline.
  useEffect(() => {
    if (!isLoading && session && !isSuperuser) {
      setError("This account does not have platform admin access.");
    }
  }, [isLoading, session, isSuperuser]);

  if (!isLoading && session && isSuperuser) {
    return <Navigate to="/" replace />;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setSubmitting(false);
    if (signInError) {
      setError(signInError.message);
    }
    // On success: AuthContext's onAuthStateChange fires, ProtectedRoute handles redirect.
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background p-4">
      <form
        onSubmit={handleLogin}
        className="w-full max-w-sm space-y-4 border border-border rounded-lg p-6 bg-card"
      >
        <h1 className="text-2xl font-semibold text-foreground">Admin Portal</h1>

        {expired && (
          <div className="text-sm text-muted-foreground border border-border rounded-md p-3 bg-muted/50">
            Session expired. Please sign in again.
          </div>
        )}

        {error && (
          <div className="text-sm text-destructive border border-destructive/30 rounded-md p-3 bg-destructive/10">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </div>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </main>
  );
}
